import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { StoreIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useDispatches } from '@/api/dispatches'
import type { Dispatch } from '@/api/dispatches'
import { useSalesChannels } from '@/api/sales-channels'
import { VoidedBadge } from '@/components/corrections/voided'
import { FilterBar } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'

// Dispatches list (UI-SCREENS I-04, 05c #171): date, Sales Channel, units, who recorded it and
// the voided badge, filterable by channel and date. Owner and Staff both see it and both may add
// one (permissions.md). The filters sit in the shared filter bar (as on I-03) and live in the URL,
// so a filtered list survives a reload and can be linked to.
const searchSchema = z.object({
  channelId: z.coerce.number().int().positive().optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/dispatches/')({
  component: DispatchesList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'dispatches.title' },
})

function DispatchesList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const channels = useSalesChannels(slug)
  const filters = { salesChannelId: search.channelId, from: search.from, to: search.to }
  const dispatches = useDispatches(slug, filters)
  const filtered = Object.values(filters).some((value) => value !== undefined)

  function set(patch: Partial<typeof search>) {
    navigate({ search: (current) => ({ ...current, ...patch }), replace: true })
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('dispatches.title')}</h1>
        <Button asChild className="h-11">
          <Link to="/w/$slug/dispatches/new" params={{ slug }}>
            {t('dispatches.new')}
          </Link>
        </Button>
      </div>

      <FilterBar
        fields={[
          {
            key: 'channelId',
            label: t('dispatches.columns.channel'),
            icon: StoreIcon,
            value: search.channelId ? String(search.channelId) : undefined,
            options: (channels.data ?? []).map((channel) => ({ value: String(channel.id), label: channel.name })),
            onChange: (value) => set({ channelId: value === undefined ? undefined : Number(value) }),
          },
        ]}
        date={{ from: search.from, to: search.to }}
        onDateChange={(range) => set({ from: range.from, to: range.to })}
        onClearAll={() => set({ channelId: undefined, from: undefined, to: undefined })}
      />

      {dispatches.isPending ? (
        <LoadingSkeleton />
      ) : dispatches.isError || !dispatches.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : dispatches.data.length === 0 ? (
        <RecordListEmpty>{t(filtered ? 'dispatches.emptyFiltered' : 'dispatches.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {dispatches.data.map((dispatch) => (
            <DispatchRow key={dispatch.id} slug={slug} dispatch={dispatch} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function DispatchRow({ slug, dispatch }: { slug: string; dispatch: Dispatch }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      voided={Boolean(dispatch.voided_at)}
      title={
        <RecordLink to="/w/$slug/dispatches/$dispatchId" params={{ slug, dispatchId: String(dispatch.id) }}>
          {dispatch.dispatch_no}
        </RecordLink>
      }
      badges={<VoidedBadge record={dispatch} />}
      meta={[
        { icon: StoreIcon, label: t('dispatches.columns.channel'), value: dispatch.sales_channel.name },
        dispatch.created_by
          ? {
              icon: UserIcon,
              label: t('dispatches.columns.createdBy'),
              value: dispatch.created_by.name ?? dispatch.created_by.email,
            }
          : null,
      ]}
      figure={t('dispatches.units', { count: dispatch.total_units })}
      caption={formatBusinessDate(dispatch.occurred_on)}
    />
  )
}
